import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { services } from "../data/services";
import ServiceCard from "../components/ServiceCard";
import CategoryFilter from "../components/CategoryFilterservice";
import { Clock, Users, Star, Award, Zap, CheckCircle } from "lucide-react";

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [isLoading, setIsLoading] = useState(true);

  // 👇 Scroll siempre al inicio cuando se carga el componente
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // 👇 Simular carga de servicios
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const filteredAndSortedServices = useMemo(() => {
    let filtered = services;

    if (selectedCategory !== "all") {
      filtered = services.filter(
        (service) => service.category === selectedCategory
      );
    }

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return a.price - b.price;
        case "price-high":
          return b.price - a.price;
        case "rating":
          return b.rating - a.rating;
        case "duration":
          return a.duration - b.duration;
        case "clients":
          return b.clients - a.clients;
        case "name":
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }, [selectedCategory, sortBy]);

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/20 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="w-20 h-20 border-4 border-teal-200 border-t-teal-600 rounded-full mx-auto mb-4 animate-spin"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-2xl font-bold text-gray-900 mb-2"
          >
            Cargando Servicios
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-gray-600"
          >
            Preparando nuestras soluciones profesionales para ti...
          </motion.p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* HEADER MEJORADO */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 relative"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-full text-sm font-medium inline-flex items-center gap-2 mb-6 shadow-lg"
          >
            <Zap className="w-4 h-4" />
            Servicios Profesionales
          </motion.span>
          <h1 className="text-4xl lg:text-6xl font-extrabold text-gray-900 mb-6">
            Nuestros{" "}
            <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
              Servicios
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Soluciones profesionales diseñadas para impulsar tu negocio al siguiente nivel. 
            <span className="block text-teal-600 font-semibold mt-2">
              Encuentra el servicio perfecto para tus necesidades.
            </span>
          </p>

          {/* STATS HEADER */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-wrap justify-center gap-6 mt-12"
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-teal-600">{services.length}+</div>
              <div className="text-gray-500 text-sm">Servicios</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {services.reduce((acc, service) => acc + service.clients, 0)}+
              </div>
              <div className="text-gray-500 text-sm">Clientes Satisfechos</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {Math.max(...services.map(s => s.rating))}
              </div>
              <div className="text-gray-500 text-sm">Rating Promedio</div>
            </div>
          </motion.div>
        </motion.div>

        {/* CATEGORY FILTER */}
        <CategoryFilter
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {/* SORT + COUNT MEJORADO */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-10 bg-white/80 backdrop-blur-lg px-6 py-4 rounded-2xl shadow-lg border border-gray-100"
        >
          <div className="flex items-center gap-4">
            <div className="text-gray-700 text-sm sm:text-base font-medium">
              Mostrando <span className="font-bold text-teal-600">{filteredAndSortedServices.length}</span> servicios
            </div>
            {selectedCategory !== "all" && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => setSelectedCategory("all")}
                className="px-3 py-1 bg-teal-100 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-200 transition-colors"
              >
                Limpiar filtro
              </motion.button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 font-medium">Ordenar por:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition shadow-sm"
            >
              <option value="name">Nombre (A-Z)</option>
              <option value="price-low">Precio: Menor a Mayor</option>
              <option value="price-high">Precio: Mayor a Menor</option>
              <option value="rating">Mejor Calificación</option>
              <option value="duration">Duración</option>
              <option value="clients">Más Popular</option>
            </select>
          </div>
        </motion.div>

        {/* SERVICE GRID */}
        <AnimatePresence mode="wait">
          {filteredAndSortedServices.length > 0 ? (
            <motion.div
              key="services-grid"
              layout
              className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8"
            >
              {filteredAndSortedServices.map((service, index) => (
                <motion.div
                  key={service.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    delay: index * 0.1, 
                    duration: 0.5,
                    type: "spring",
                    stiffness: 100
                  }}
                >
                  <ServiceCard service={service} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="no-services"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-24 bg-white/50 rounded-3xl backdrop-blur-sm"
            >
              <div className="text-8xl mb-8">🔍</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                No se encontraron servicios
              </h3>
              <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
                No hay servicios disponibles en esta categoría. Prueba con otros filtros o explora todas nuestras soluciones.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedCategory("all");
                  setSortBy("name");
                }}
                className="px-8 py-4 bg-gradient-to-r from-teal-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Ver Todos los Servicios
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* FEATURES SECTION MEJORADA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-24"
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Por qué elegir nuestros{" "}
              <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                servicios?
              </span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprometidos con la excelencia y resultados tangibles para tu negocio
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Entrega Rápida</h3>
              <p className="text-gray-600 leading-relaxed">
                Servicios eficientes con tiempos de entrega optimizados sin comprometer la calidad del resultado final.
              </p>
            </motion.div>
            
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Soporte Dedicado</h3>
              <p className="text-gray-600 leading-relaxed">
                Atención personalizada durante todo el proceso con expertos disponibles para resolver tus dudas.
              </p>
            </motion.div>
            
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Calidad Garantizada</h3>
              <p className="text-gray-600 leading-relaxed">
                Resultados profesionales con los más altos estándares de calidad y satisfacción garantizada.
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* CTA SECTION */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-20 text-center bg-gradient-to-r from-teal-600 to-blue-600 rounded-3xl p-12 text-white"
        >
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            ¿Listo para impulsar tu negocio?
          </h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Contáctanos hoy mismo y descubre cómo nuestros servicios pueden transformar tu presencia digital.
          </p>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 bg-white text-teal-600 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
          >
            Solicitar Consulta Gratuita
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;